import { useState } from 'react';
import { IconPlus } from '@iwoca/icons';
import { Input, Label, Tooltip } from '@iwoca/orion';
import clsx from 'clsx';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { InformationMessage } from 'components/InformationMessage/InformationMessage';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useFormContext } from 'react-hook-form';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { softValidateEmail } from '../../../utils/emailAddressHelper';
import { useSignupData } from '../hooks/useSignupData';
import { MultiAddressInput } from '../MultiAddressInput/MultiAddressInput';
import { TSignupFormData } from '../SignupForm/SignupForm';

import signupFormStyles from '../SignupForm/SignupForm.module.css';
import styles from './PersonalDetails.module.css';

export type PersonalDetailsType = {
  role: 'applicant' | 'guarantor';
  index?: number;
};

export const PersonalDetails = ({ role, index = 0 }: PersonalDetailsType) => {
  const { trackEvent } = useAmplitude();
  const { isReintroduction } = useSignupData();
  const {
    register,
    formState: { errors },
  } = useFormContext<TSignupFormData>();

  const [secondaryPhoneNumberIsVisible, setSecondaryPhoneNumberIsVisible] =
    useState(false);

  const [showEmailWarning, setShowEmailWarning] = useState(false);

  const softValidateEmailAddress = (value: string) => {
    const emailIsValid = softValidateEmail(value);

    setShowEmailWarning(!emailIsValid);
  };

  const PERSON_PROPERTY =
    role === 'applicant' ? 'applicant' : (`guarantors.${index}` as const);

  const ERROR_PROPERTY =
    role === 'applicant'
      ? errors.applicant
      : errors?.[role === 'guarantor' ? 'guarantors' : role]?.[index];

  const { checkIntroducerPermission } = useIntroducerContext();
  const isDisabled =
    !checkIntroducerPermission('can_create_applications') || isReintroduction;

  return (
    <div className={signupFormStyles.cardContents}>
      <div className={signupFormStyles.formField}>
        <Label>Date of birth</Label>
        <div className={styles.dobContainer}>
          <Input
            {...register(`${PERSON_PROPERTY}.dobDay`)}
            placeholder="DD"
            data-testid="dob-day"
            error={ERROR_PROPERTY?.dobDay?.message}
            disabled={isDisabled}
            className={clsx('fs-exclude', 'dd-privacy-mask')}
          />
          <Input
            {...register(`${PERSON_PROPERTY}.dobMonth`)}
            placeholder="MM"
            data-testid="dob-month"
            error={ERROR_PROPERTY?.dobMonth?.message}
            disabled={isDisabled}
            className={clsx('fs-exclude', 'dd-privacy-mask')}
          />
          <Input
            {...register(`${PERSON_PROPERTY}.dobYear`)}
            placeholder="YYYY"
            data-testid="dob-year"
            error={ERROR_PROPERTY?.dobYear?.message}
            disabled={isDisabled}
            className={clsx('fs-exclude', 'dd-privacy-mask')}
          />
        </div>
      </div>
      <div className={signupFormStyles.formField}>
        <div className={signupFormStyles.labelWithTooltip}>
          <Label htmlFor={`${PERSON_PROPERTY}.phoneNumber`}>
            Mobile phone number
          </Label>
          <Tooltip content="We'll only contact the person with your permission." />
        </div>
        <Input
          {...register(`${PERSON_PROPERTY}.phoneNumber`)}
          className={clsx(
            'fs-exclude',
            'dd-privacy-mask',
            styles.inputWithAddNumberButton,
          )}
          id={`${PERSON_PROPERTY}.phoneNumber`}
          type="text"
          placeholder="e.g. 07700 900000"
          error={ERROR_PROPERTY?.phoneNumber?.message}
          disabled={isDisabled}
        />
        {!secondaryPhoneNumberIsVisible && (
          <ButtonLink
            type="button"
            className={clsx(styles.addNumberButton, {
              [styles.isDisabled]: isDisabled,
              [styles.addNumberButtonWithError]:
                ERROR_PROPERTY?.phoneNumber?.message,
            })}
            onClick={() => {
              setSecondaryPhoneNumberIsVisible(true);
              trackEvent(AMPLITUDE_EVENTS.extraDetailsAdded, {
                'Details added': 'Phone number',
                'CTA text': 'Add another number',
              });
            }}
            disabled={isDisabled}
          >
            <IconPlus />
            Add another number
          </ButtonLink>
        )}
      </div>
      {secondaryPhoneNumberIsVisible && (
        <div className={signupFormStyles.formField}>
          <div className={styles.secondaryNumberContainer}>
            <Input
              {...register(`${PERSON_PROPERTY}.secondaryPhoneNumber`)}
              data-testid="secondary-number-input"
              type="text"
              error={ERROR_PROPERTY?.secondaryPhoneNumber?.message}
              placeholder="e.g. 07700 900000"
              className={clsx('fs-exclude', 'dd-privacy-mask')}
            />
            <ButtonLink
              type="button"
              className={styles.secondaryNumberCancel}
              data-testid="secondary-number-cancel-btn"
              onClick={() => {
                setSecondaryPhoneNumberIsVisible(false);
                trackEvent(AMPLITUDE_EVENTS.extraDetailsRemoved, {
                  'Details removed': 'Phone number',
                  'CTA text': '',
                });
              }}
            >
              <IconPlus height={25} width={25} className={styles.cancelIcon} />
            </ButtonLink>
          </div>
        </div>
      )}
      <div className={signupFormStyles.formField}>
        <div className={signupFormStyles.labelWithTooltip}>
          <Label htmlFor={`${PERSON_PROPERTY}.email`}>
            {role === 'applicant'
              ? `Applicant's direct email address`
              : 'Direct email address'}
          </Label>
          <Tooltip content="We need an email address that only this individual can access." />
        </div>
        <Input
          {...register(`${PERSON_PROPERTY}.email`)}
          id={`${PERSON_PROPERTY}.email`}
          type="email"
          placeholder="e.g. firstname@example.com"
          onBlur={(e) => softValidateEmailAddress(e.target.value)}
          error={ERROR_PROPERTY?.email?.message}
          disabled={isDisabled}
          className={clsx('fs-exclude', 'dd-privacy-mask')}
        />
        {showEmailWarning && !ERROR_PROPERTY?.email?.message && (
          <InformationMessage message="Please ensure the email address is only accessible by the individual." />
        )}
      </div>
      <MultiAddressInput personRole={role} personIndex={index} />
    </div>
  );
};
